import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../services/adminAPI';
import Navbar from '../components/Navbar';
import './AdminAgeGroups.css';

function AdminAgeGroups() {
  const navigate = useNavigate();
  const [ageGroups, setAgeGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingAgeGroup, setEditingAgeGroup] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    displayOrder: 0,
    isActive: true,
  });

  useEffect(() => {
    fetchAgeGroups();
  }, []);

  const fetchAgeGroups = async () => {
    try {
      const response = await adminAPI.getAllAgeGroups();
      setAgeGroups(response.data);
    } catch (error) {
      console.error('Error fetching age groups:', error);
      alert('Failed to load age groups');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAgeGroup = async (e) => {
    e.preventDefault();

    try {
      await adminAPI.createAgeGroup(formData);
      alert('Age group created successfully!');
      setShowCreateModal(false);
      resetForm();
      fetchAgeGroups();
    } catch (error) {
      console.error('Create age group error:', error);
      alert(error.response?.data?.error || 'Failed to create age group');
    }
  };

  const handleUpdateAgeGroup = async (e) => {
    e.preventDefault();

    try {
      await adminAPI.updateAgeGroup(editingAgeGroup.id, formData);
      alert('Age group updated successfully!');
      setEditingAgeGroup(null);
      resetForm();
      fetchAgeGroups();
    } catch (error) {
      console.error('Update age group error:', error);
      alert(error.response?.data?.error || 'Failed to update age group');
    }
  };

  const handleDeleteAgeGroup = async (id, name) => {
    if (!window.confirm(`Delete age group "${name}"? This cannot be undone.`)) {
      return;
    }

    try {
      await adminAPI.deleteAgeGroup(id);
      alert('Age group deleted successfully!');
      fetchAgeGroups();
    } catch (error) {
      console.error('Delete age group error:', error);
      alert(error.response?.data?.error || 'Failed to delete age group');
    }
  };

  const openEditModal = (ageGroup) => {
    setEditingAgeGroup(ageGroup);
    setFormData({
      name: ageGroup.name,
      displayOrder: ageGroup.displayOrder,
      isActive: ageGroup.isActive,
    });
  };

  const resetForm = () => {
    setFormData({
      name: '',
      displayOrder: 0,
      isActive: true,
    });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="admin-container">
          <div className="loading">Loading age groups...</div>
        </div>
      </>
    );
  }

  return (
    <div className="admin-page-wrapper">
      <Navbar />
      <div className="admin-container">
        <button onClick={() => navigate('/admin')} className="back-button">
          ← Back to Admin Home
        </button>
        <div className="admin-header">
          <h1>Manage Age Groups</h1>
          <p>Configure available age groups for team creation</p>
        </div>

        <div className="admin-actions">
          <button
            className="btn btn-primary"
            onClick={() => setShowCreateModal(true)}
          >
            + Create Age Group
          </button>
        </div>

        <div className="items-list">
          <div className="list-header">
            <div className="header-col">Name</div>
            <div className="header-col">Display Order</div>
            <div className="header-col">Status</div>
            <div className="header-col">Actions</div>
          </div>

          {ageGroups.map((ageGroup) => (
            <div key={ageGroup.id} className="list-item">
              <div className="item-col">
                <strong>{ageGroup.name}</strong>
              </div>
              <div className="item-col">{ageGroup.displayOrder}</div>
              <div className="item-col">
                <span
                  className={`status-badge ${
                    ageGroup.isActive ? 'active' : 'inactive'
                  }`}
                >
                  {ageGroup.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div className="item-col actions">
                <button
                  className="btn-edit"
                  onClick={() => openEditModal(ageGroup)}
                >
                  Edit
                </button>
                <button
                  className="btn-delete"
                  onClick={() =>
                    handleDeleteAgeGroup(ageGroup.id, ageGroup.name)
                  }
                >
                  Delete
                </button>
              </div>
            </div>
          ))}

          {ageGroups.length === 0 && (
            <div className="empty-state">
              <p>No age groups found. Create one to get started!</p>
            </div>
          )}
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowCreateModal(false)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create Age Group</h2>
              <button
                className="modal-close"
                onClick={() => setShowCreateModal(false)}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleCreateAgeGroup}>
              <div className="form-group">
                <label>Age Group Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="e.g., 10U, T-Ball, High School"
                  required
                />
              </div>

              <div className="form-group">
                <label>Display Order *</label>
                <input
                  type="number"
                  name="displayOrder"
                  value={formData.displayOrder}
                  onChange={handleChange}
                  min="0"
                  required
                />
                <small>Lower numbers appear first in dropdowns</small>
              </div>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleChange}
                  />
                  Active (available for team creation)
                </label>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => setShowCreateModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Create Age Group
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingAgeGroup && (
        <div className="modal-overlay" onClick={() => setEditingAgeGroup(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Age Group</h2>
              <button
                className="modal-close"
                onClick={() => setEditingAgeGroup(null)}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleUpdateAgeGroup}>
              <div className="form-group">
                <label>Age Group Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>Display Order *</label>
                <input
                  type="number"
                  name="displayOrder"
                  value={formData.displayOrder}
                  onChange={handleChange}
                  min="0"
                  required
                />
                <small>Lower numbers appear first in dropdowns</small>
              </div>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleChange}
                  />
                  Active (available for team creation)
                </label>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => setEditingAgeGroup(null)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Update Age Group
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminAgeGroups;
